import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { MAX_INVITE_EMAILS } from '@gitbook/app-database';
import {
    Button,
    Description,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    FieldGroup,
    intlPluralizeString,
    Label,
    Switch,
    toast,
    useDialogClose,
} from '@gitbook/spine-react';

import {
    BulkInviteLimitAlert,
    PERMISSION_ROLES_DESCRIPTORS,
    PermissionRoleButton,
} from '../../../../Components';
import type {
    EmailTag,
    PermissionTag,
    TeamTag,
    UserTag,
} from '../../../../Components/ShareContent/InviteMembers/state';
import { UserTagInput } from '../../../../Components/Users';
import { apiClient, apiQuery, useSiteContext } from '../../../../state';
import { SITE_PERMISSION_ROLE_GROUPS } from './types';

/**
 * Invite users/teams directly to a site.
 *
 * Renders the dialog body (invite input) and footer (Invite action) as siblings so the
 * primary action sits in the dialog footer, like the organization invite dialog.
 * The browsable list of who already has access (and the site default permission)
 * lives on the dedicated Site Members screen.
 */
export function SitePermissionsList(): React.ReactElement {
    const { organizationId, site } = useSiteContext();

    const { data: organization } = useSuspenseQuery(
        apiQuery('getOrganizationById', { organizationId })
    );
    const canAddGuests = organization.permissions.admin;

    // Writes the site permission. Site mode is ensured by the caller before any invite runs.
    const onInvite = React.useCallback(
        async (input: { role: api.MemberRoleOrGuest; users: string[]; teams: string[] }) => {
            let invited = false;
            await toast.catch(async () => {
                await apiClient.sitePermissions.inviteToSite(organizationId, site.id, input);
                invited = true;
            }, 'Error while inviting users');
            return invited;
        },
        [organizationId, site.id]
    );

    const [tags, setTags] = React.useState<PermissionTag[]>([]);
    const [permission, setPermission] = React.useState<api.MemberRoleOrGuest>(api.MemberRole.Edit);
    const [inviteAsOrgMember, setInviteAsOrgMember] = React.useState(true);
    const closeDialog = useDialogClose();

    const { emails, users, teams } = React.useMemo(() => {
        return {
            emails: tags.filter((tag): tag is EmailTag => tag.type === 'email'),
            users: tags.filter((tag): tag is UserTag => tag.type === 'user'),
            teams: tags.filter((tag): tag is TeamTag => tag.type === 'team'),
        };
    }, [tags]);
    const organizationInviteRole =
        permission === null ? null : PERMISSION_ROLES_DESCRIPTORS[permission];
    const inviteEmailsAsOrgMember = inviteAsOrgMember && organizationInviteRole !== null;

    const onSubmit = React.useCallback(async () => {
        if (emails.length > MAX_INVITE_EMAILS) {
            toast.error('Error while updating the permissions', null, {
                description: `You can only invite up to ${MAX_INVITE_EMAILS} emails at once.`,
            });
            return;
        }

        let usersToAdd = users.map((u) => u.user);
        let guestsCount = 0;

        if (emails.length > 0) {
            await toast.catch(async () => {
                const { data } = await apiClient.organizationInvites.inviteUsersToOrganization(
                    organizationId,
                    {
                        emails: emails.map((emailTag) => emailTag.email),
                        role: inviteEmailsAsOrgMember ? permission : null,
                    }
                );

                usersToAdd = usersToAdd.concat(...data.users);
                if (!inviteEmailsAsOrgMember) {
                    guestsCount = data.invited;
                }
            }, 'Error while inviting users');
        }

        const invited = await onInvite({
            role: permission,
            users: usersToAdd,
            teams: teams.map((team) => team.team),
        });

        if (!invited) {
            return;
        }

        const usersCount = usersToAdd.length - guestsCount;

        toast.success(
            `${intlPluralizeString({
                count: usersCount,
                singular: 'Invite',
                plural: 'Invites',
            })} sent`
        );

        closeDialog();
    }, [
        closeDialog,
        emails,
        inviteEmailsAsOrgMember,
        onInvite,
        organizationId,
        permission,
        teams,
        users,
    ]);

    return (
        <>
            <DialogBody>
                <div className="flex flex-col gap-6">
                    <UserTagInput
                        organization={organizationId}
                        canInviteUsers={canAddGuests}
                        value={tags}
                        onChangeValue={setTags}
                        button={
                            <PermissionRoleButton
                                size="small"
                                kind="blank"
                                roleGroups={SITE_PERMISSION_ROLE_GROUPS}
                                value={permission}
                                onValueChange={setPermission}
                                icon={null}
                                // No "Deny access" when inviting — denying happens from the members
                                // list. Inviting someone with no access proved to be confusing for
                                // lots of users.
                                allowNullValue={false}
                            />
                        }
                    />

                    <BulkInviteLimitAlert emailCount={emails.length} />

                    {canAddGuests && emails.length > 0 ? (
                        <div className="rounded-sm border border-card bg-secondary px-4 py-2">
                            <FieldGroup orientation="horizontal">
                                <FieldGroup orientation="vertical">
                                    <Label>Add email invitees to the organization</Label>
                                    <Description className="font-normal text-muted">
                                        {organizationInviteRole ? (
                                            inviteEmailsAsOrgMember ? (
                                                <>
                                                    New email addresses will join the organization
                                                    as{' '}
                                                    <strong>{organizationInviteRole.title}</strong>{' '}
                                                    and receive the same role on this site.
                                                </>
                                            ) : (
                                                <>
                                                    New email addresses will join the organization
                                                    as <strong>Guests</strong> and only receive the{' '}
                                                    <strong>{organizationInviteRole.title}</strong>{' '}
                                                    role on this site.
                                                </>
                                            )
                                        ) : (
                                            'Choose a site role to add new email addresses to the organization.'
                                        )}
                                    </Description>
                                </FieldGroup>
                                <Switch
                                    aria-label="Add email invitees to the organization"
                                    value={inviteEmailsAsOrgMember}
                                    disabled={organizationInviteRole === null}
                                    onValueChange={setInviteAsOrgMember}
                                />
                            </FieldGroup>
                        </div>
                    ) : null}
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton label="Cancel" />
                <Button
                    kind="primary"
                    size="medium"
                    disabled={tags.length === 0}
                    onPress={onSubmit}
                    label="Invite"
                />
            </DialogFooter>
        </>
    );
}
